<template>
  <v-card class="p-0" rounded="xl" style="position: relative">
    <v-overlay :value="!hasPermission" absolute z-index="2" opacity="0.3"></v-overlay>
    <div class="px-2 px-md-7 pt-5 pb-2">
      <div class="d-flex justify-space-between align-center">
        <h6 class="text-subtitle-1 font-weight-bold">衛教影音（橫式）</h6>

        <v-btn
          v-if="!isEditing"
          color="teal lighten-2"
          class="text-subtitle-2 rounded-lg"
          dark
          style="width: 100px; height: 40px"
          @click="startEdit"
        >
          <v-icon dark size="15" class="mr-2">mdi-pencil</v-icon>
          編輯
        </v-btn>
        <div v-else class="d-flex align-center justify-end flex-shrink-1" style="gap: 12px;">
          <v-btn v-text="'取消'" color="grey" outlined class="text-subtitle-2 rounded-lg" :disabled="isSaving" @click="cancelEdit"/>
          <v-btn
            v-text="'儲存'"
            color="teal lighten-2"
            class="text-subtitle-2 rounded-lg white--text"
            :disabled="isSaving"
            :loading="isSaving"
            @click="saveEdit"
          />
        </div>
      </div>

      <div v-if="!isEditing" class="d-flex my-2 overflow-x-auto" style="gap: 12px;">
        <v-sheet v-if="!videos.length" height="250" width="100%" class="d-flex align-center justify-center">
          <div class="grey-4--text">
            影音有助於增加診所的品牌力，獲取NP的信任和預約，<br>
            若需專業攝影協助協助，請洽「<a :href="$enum.DENTCO_CS_LINE" target="_blank"
                                         class="link_blue--text">牙醫小幫手客服</a>」
          </div>
        </v-sheet>
        <div
          v-else
          v-for="(item, i) in videos"
          :key="i"
          class="py-3 flex-shrink-0"
          style="max-width: 600px; width: 100%;"
          :style="{minHeight: $vuetify.breakpoint.smAndDown ? '250px' : '338px'}"
        >
          <component
            :is="getVideoComponent(item.platform)"
            :url="item.url"
          />
        </div>
      </div>

      <v-row v-else no-gutters class="my-2 py-2" style="row-gap: 28px;">
        <v-sheet ref="scrollContainer" height="359" width="100%" class="overflow-y-auto pr-2">
          <v-form ref="form" v-model="valid">
            <draggable
              v-model="editList"
              tag="div"
              handle=".drag-content"
              class="d-flex flex-column"
              style="row-gap: 16px;"
            >
              <v-sheet
                v-for="(item, index) in editList"
                :key="index"
                color="grey-8"
                class="py-5"
                rounded
              >
                <v-row no-gutters class="align-center align-md-start">
                  <v-col cols="2" md="1" class="text-center">
                    <v-btn icon class="drag-content">
                      <v-icon>mdi-cursor-move</v-icon>
                    </v-btn>
                  </v-col>
                  <v-col cols="10" md="11">
                    <v-row no-gutters class="d-flex" style="row-gap: 8px;">
                      <v-col cols="12" lg="3" class="px-1">
                        <v-select
                          v-model="item.platform"
                          :items="platformOptions"
                          item-text="label"
                          item-value="value"
                          :rules="[v => !!v || '請選擇影音平台']"
                          dense outlined full-width background-color="white"
                          hide-details="auto"
                        />
                      </v-col>
                      <v-col cols="10" lg="8" class="px-1">
                        <v-text-field
                          v-model="item.url"
                          :ref="`url-${index}`"
                          dense outlined full-width background-color="white"
                          :rules="urlRules(item)"
                          :placeholder="showPlaceholder()"
                          hide-details="auto"
                        />
                      </v-col>
                      <v-col cols="2" lg="1" class="px-1">
                        <v-btn icon @click="removeItem(index)">
                          <v-icon>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-sheet>
            </draggable>
          </v-form>
        </v-sheet>

        <div class="d-flex align-center" style="width: 100%; gap: 20px;">
          <div class="flex-grow-1">
            <v-btn
              block
              color="grey-8"
              :dark="editList.length < maxVideos"
              style="color: var(--v-grey-5-base);"
              :disabled="editList.length >= maxVideos"
              @click="addItem"
            >
              <template v-if="editList.length < maxVideos">
                <v-icon small class="mr-1">mdi-plus</v-icon>
                點我新增衛教影音
              </template>
              <template v-else>
                已達上限 {{ maxVideos }} 支影音
              </template>
            </v-btn>
          </div>

          <div class="d-flex align-center justify-end flex-shrink-1" style="gap: 12px;">
            <v-btn v-text="'取消'" color="grey" outlined class="text-subtitle-2 rounded-lg" :disabled="isSaving" @click="cancelEdit"/>
            <v-btn
              v-text="'儲存'"
              color="teal lighten-2"
              class="text-subtitle-2 rounded-lg white--text"
              :disabled="isSaving"
              :loading="isSaving"
              @click="saveEdit"
            />
          </div>
        </div>
      </v-row>
    </div>
  </v-card>
</template>

<script>
import draggable from 'vuedraggable';
import { getClinicVideosAPI, saveClinicRegularVideosAPI } from '@/api';
import { mapGetters } from 'vuex';
import YoutubeIframe from "@/components/settings/doctor/v2/YoutubeIframe.vue";
import { doctorHealthVideos } from "@/api/mockData/tour/onboarding";
import {
  normalizeYoutubeUrl,
  cleanRegularVideoUrl,
  validateRegularVideoUrl,
  getRegularVideoPlaceholder
} from "@/utils/videoUrlHelper";

export default {
  name: 'ClinicRegularVideoCard',

  props: {
    hasPermission: Boolean
  },

  components: {
    YoutubeIframe,
    draggable
  },

  data() {
    const defaultVideos = [
      {
        platform: 'youtube',
        url: 'https://www.youtube.com/watch?v=CEbF0cWr5es'
      }
    ]
    return {
      defaultVideos,
      videos: [],
      editList: [],
      valid: true,
      maxVideos: 10,
      isEditing: false,
      isSaving: false
    };
  },

  computed: {
    ...mapGetters(['axiosParamData']),
    ...mapGetters('tour', {
      currentTourName: 'currentTourName'
    }),
    platformOptions() {
      return [
        {label: 'YouTube', value: 'youtube'}
      ];
    }
  },

  mounted() {
    if (this.currentTourName === 'onboarding') {
      this.videos = doctorHealthVideos.regular
    } else {
      this.getClinicVideos();
    }
  },

  methods: {
    getVideoComponent(platform) {
      switch (platform) {
        case 'youtube':
          return 'YoutubeIframe';
        default:
          return 'div'; // fallback
      }
    },
    showPlaceholder() {
      return getRegularVideoPlaceholder();
    },
    validateUrl(item) {
      return validateRegularVideoUrl(item.url, item.platform);
    },
    urlRules(item) {
      return [
        v => !v || v.trim() !== '' || '請輸入影音網址',
        v => this.validateUrl(item)
      ];
    },
    async getClinicVideos() {
      const res = await getClinicVideosAPI(this.axiosParamData);
      this.videos = res.data?.regular || (this.hasPermission ? [] : this.defaultVideos);
    },
    startEdit() {
      this.editList = JSON.parse(JSON.stringify(this.videos));
      this.isEditing = true;
    },
    cancelEdit() {
      this.isEditing = false;
    },
    saveEdit() {
      this.$refs.form.validate();
      if (!this.valid || this.isSaving) return;

      this.isSaving = true;

      const cleaned = this.editList.map(item => {
        // 先標準化網址 (例: youtu.be -> youtube.com/watch)
        const normalized = item.platform === 'youtube'
          ? normalizeYoutubeUrl(item.url)
          : item.url;

        return {
          ...item,
          url: cleanRegularVideoUrl(normalized, item.platform),
          type: 'regular'
        };
      });

      saveClinicRegularVideosAPI({
        ...this.axiosParamData,
        videos: cleaned
      }).then(() => {
        this.isEditing = false;
        this.getClinicVideos();
      }).finally(() => {
        this.isSaving = false;
      });
    },
    addItem() {
      if (this.editList.length >= this.maxVideos) return;
      this.editList.push({platform: 'youtube', url: '', type: 'long'});
    },
    removeItem(index) {
      this.editList.splice(index, 1);
    }
  }
};
</script>

<style scoped>
/* optional scoped style */
</style>
